import {
  DeleteOutlined,
  EditTwoTone,
  PlusCircleOutlined,
} from "@ant-design/icons";
import { Button, Flex } from "@p0security/ui";
import { useMemo } from "react";

type Props<T> = {
  addButtonDisabled?: boolean;
  addButtonText: string;
  editing?: boolean;
  items: T[];
  minItems?: number;
  onAddItem: () => void;
  onDeleteItem: (index: number) => void;
  onEditItem: (index: number) => void;
  renderItem: (item: T, index: number) => React.ReactNode;
};

export const EditableList = <T,>({
  addButtonDisabled = false,
  addButtonText,
  editing = false,
  items,
  minItems = 0,
  onAddItem,
  onDeleteItem,
  onEditItem,
  renderItem,
}: Props<T>) => {
  const deleteCallbacks = useMemo(
    () => items.map((_item, ix) => () => onDeleteItem(ix)),
    [items, onDeleteItem]
  );
  const editCallbacks = useMemo(
    () => items.map((_item, ix) => () => onEditItem(ix)),
    [items, onEditItem]
  );

  const canDelete = items.length > minItems;

  return (
    <Flex vertical gap={8} align="flex-start" className="w-full">
      {items.map((item, index) => (
        <Flex key={index} align="center" gap={8} className="w-full">
          <Flex flex="1">{renderItem(item, index)}</Flex>
          {!editing && (
            <Flex gap={4}>
              <Button
                type="text"
                onClick={editCallbacks[index]}
                icon={<EditTwoTone />}
              />
              {canDelete && (
                <Button
                  type="text"
                  danger
                  onClick={deleteCallbacks[index]}
                  icon={<DeleteOutlined />}
                />
              )}
            </Flex>
          )}
        </Flex>
      ))}
      {!editing && (
        <Button
          type="link"
          size="small"
          onClick={onAddItem}
          icon={<PlusCircleOutlined />}
          disabled={addButtonDisabled}
          className="px-0!"
        >
          {addButtonText}
        </Button>
      )}
    </Flex>
  );
};
